/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { fetchAllAirtableRecords } from "./client";
import { normalizeIndustries } from "./industry-map";
import { normalizeWebsiteUrl } from "./url";

import type { AirtableRecord } from "./client";
import type { Instructor } from "./types";

const BASE_ID = "appTXNGNPznnzwjC0";
const TABLE_ID = "tbletnEbnfzJtXWsO";
const GOLD_FILTER = "{2026 Badge}='GOLD'";

interface InstructorFields {
	"UAI Candidate"?: string;
	Country?: string;
	Industry?: string;
	GEO?: string;
	"2026 Badge"?: string;
	"LinkedIn URL"?: string;
	"EDC URL"?: string;
	"ArtStation URL"?: string;
	"YEAR Qualified"?: string;
	ID?: number;
}

/**
 * Map a single Airtable record to an `Instructor`. Returns `null` for rows
 * missing the display name — same convention as `mapRecord` in
 * `training-partners.ts` so blank `<h3>` cards never reach the grid.
 */
function mapRecord(record: AirtableRecord<InstructorFields>): Instructor | null {
	const f = record.fields;
	const name = f["UAI Candidate"];
	// Skip rows without a display name — those are incomplete entries that
	// would render an empty heading on the card.
	if (!name) return null;
	return {
		// Use Airtable's row id (`recXXX`) as the canonical React key. The
		// editor-managed numeric `ID` field can collide if an editor enters
		// a duplicate value or leaves it blank; the `recXXX` is guaranteed
		// unique per row by Airtable. The numeric `ID` is preserved as
		// display metadata via the existing fields if a consumer needs it.
		id: record.id,
		name,
		country: f.Country ?? "",
		industries: normalizeIndustries(f.Industry ?? ""),
		geo: f.GEO ?? "",
		badgeLevel: f["2026 Badge"] ?? "",
		linkedInUrl: normalizeWebsiteUrl(f["LinkedIn URL"]),
		edcUrl: normalizeWebsiteUrl(f["EDC URL"]),
		artStationUrl: normalizeWebsiteUrl(f["ArtStation URL"]),
		yearQualified: f["YEAR Qualified"] || undefined,
	};
}

/**
 * Fetch every Gold-certified instructor from the UAI Web Directory base.
 *
 * Legacy: the website no longer calls this. It reads the CDN copy baked by
 * s3-sync-service, which lists Gold, Silver and Bronze from `Current Badge`.
 *
 * Requires `AIRTABLE_INSTRUCTORS_PAT` to be set; throws if missing
 * (fail-loud so vault misconfiguration is visible).
 */
export async function fetchAllGoldInstructors(): Promise<Instructor[]> {
	const records = await fetchAllAirtableRecords<InstructorFields>({
		baseId: BASE_ID,
		tableId: TABLE_ID,
		pat: process.env.AIRTABLE_INSTRUCTORS_PAT,
		filterByFormula: GOLD_FILTER,
		consumerLabel: "Gold Instructors",
		envVarName: "AIRTABLE_INSTRUCTORS_PAT",
	});
	return records.map(mapRecord).filter((r): r is Instructor => r !== null);
}
